import React from 'react';
import PropTypes from 'prop-types';
import { TILE_LIBRARY_TYPES } from '../../../state/map/tiles/constants';
import { createHereText, createLeafletIcon, createHerePoint } from './icons';
import { isEqual } from 'lodash';
import { latLng } from 'leaflet';
import './distanceMeasurement.scss';
import { calculateBearingFromPixels, interpolateLine } from '../../utils';

function DistanceMeasurement(MarkerComponent, LinesComponent, MapType) {
  return class extends React.Component {
    static propTypes = {
      options: PropTypes.object,
      fields: PropTypes.object,
      result: PropTypes.object,
      colorPalette: PropTypes.object,
      bounds: PropTypes.object,
      tabIndex: PropTypes.number.isRequired,
      selectedTab: PropTypes.number.isRequired,
      setFields: PropTypes.func.isRequired,
    };

    distanceMeasurement = (evt) => {
      const { currentPointer, latlng } = evt;
      const {
        fields: { ruler },
        setFields,
        selectedTab,
        tabIndex,
        map
      } = this.props;

      if ( selectedTab === tabIndex && evt.originalEvent.button === 0 ) {
        if ( currentPointer ) {
          const coord = map.screenToGeo(currentPointer.viewportX, currentPointer.viewportY);
          ruler.push(coord);
        } else {
          ruler.push(latlng);
        }
        setFields({ruler});
      }
    }

    startMeasurement = () => {
      const {
        map
      } = this.props;

      if ( MapType === TILE_LIBRARY_TYPES.JSLA ) {
        map.addEventListener('tap', this.distanceMeasurement);
      } else if ( MapType === TILE_LIBRARY_TYPES.LEAFLET ) {
        map.on('click', this.distanceMeasurement);
      }
    }

    stopMeasurement = () => {
      const {
        map
      } = this.props;

      if ( MapType === TILE_LIBRARY_TYPES.JSLA ) {
        map.removeEventListener('tap', this.distanceMeasurement);
      } else if ( MapType === TILE_LIBRARY_TYPES.LEAFLET ) {
        map.off('click', this.distanceMeasurement);
      }
    }

    componentDidMount = () => {
      const {
        fields: { ruler },
        map
      } = this.props;
      
      if ( !!ruler && map && (map.on || map.addEventListener) ) {
        this.startMeasurement();
      }
    }

    componentDidUpdate = (prevProps) => {
      const {
        fields: { ruler },
        map
      } = this.props;

      if ( (!prevProps.fields.ruler && !!ruler) 
        || (!isEqual(prevProps.map, map) && !!ruler) 
      ) {
        this.startMeasurement();
      } else if ( !!prevProps.fields.ruler && !ruler ) {
        this.stopMeasurement();
      }
    }

    componentWillUnmount = () => {
      const {
        fields: { ruler }
      } = this.props;

      if ( !!ruler ) {
        this.stopMeasurement();
      }
    }
  
    onClickPoint = (index) => {
      const { 
        fields: { ruler },
        setFields,
      } = this.props;

      setFields({ruler: [...ruler.slice(0, index), ...ruler.slice(index+1)]});
    }

    onChangePoint = (index, evt) => {
      const { 
        fields: { ruler },
        setFields,
      } = this.props;

      ruler[index] = evt;
      setFields({ruler});
    };

    beautify = (distance) => {
      if (distance === 0) {
        return distance;
      } else if ( distance < 1000 ) {
        return (distance.toFixed(2).replace('.', ',') + ' m');
      } else {
        const formattedNumber = (distance/1000).toFixed(2)
        .replace('.', ',')
        .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        return (formattedNumber + ' km');
      }
    }

    render() {
      let {
        colorPalette,
        tabIndex,
        selectedTab,
        fields: { ruler },
        map
      } = this.props;

      ruler = ruler || [];

      let distances = [0];
      let interpolatedRuler = [];
      let firstPosition = ruler[0];
      const texts = ruler.map((point, index) => {
        let icon;
        let bearing = 0;
        if (index>0) {
          const pos = ruler[index];
          const { lat, lng } = pos;
  
          const interpolatedSegment = interpolateLine(firstPosition.lat, firstPosition.lng, lat, lng, 0.5);
          const lastPoint = interpolatedSegment[Math.floor(interpolatedSegment.length*0.8)];
          interpolatedRuler = interpolatedRuler.concat(interpolatedSegment);
          if ( MapType === TILE_LIBRARY_TYPES.JSLA ) {
            const A = new window.H.geo.Point(point.lat, point.lng);
            const B = new window.H.geo.Point(ruler[index-1].lat, ruler[index-1].lng);
            distances.push(A.distance(B));
          } else if ( MapType === TILE_LIBRARY_TYPES.LEAFLET ) {  
            const A = latLng(point.lat, point.lng);
            const B = latLng(ruler[index-1].lat, ruler[index-1].lng);
            distances.push(A.distanceTo(B));
          }

          if ( map.geoToScreen ) { // maps here api js
            const pA = map.geoToScreen(point);
            const pB = map.geoToScreen(lastPoint);

            bearing = calculateBearingFromPixels(pA.x, pA.y, pB.x, pB.y);
          } else if ( map.latLngToContainerPoint ) { // Leaflet
            const pA = map.latLngToContainerPoint([point.lat, point.lng]);
            const pB = map.latLngToContainerPoint([lastPoint.lat, lastPoint.lng]);

            bearing = calculateBearingFromPixels(pA.x, pA.y, pB.x, pB.y);
          }

          firstPosition = pos;
        }

        if ( MapType === TILE_LIBRARY_TYPES.JSLA ) {
          icon = createHereText(colorPalette.primary, this.beautify(distances.reduce((partialSum, a) => partialSum + a, 0)), bearing.toFixed(0), (index!==0 && index!==ruler.length-1));
        } else if ( MapType === TILE_LIBRARY_TYPES.LEAFLET ) {
          icon = createLeafletIcon(colorPalette.primary, this.beautify(distances.reduce((partialSum, a) => partialSum + a, 0)), 'text', bearing.toFixed(0), (index!==0 && index!==ruler.length-1));
        }

        return <MarkerComponent 
          key={index+'texts'} 
          icon={icon}
          hideOnDrag={true}
          position={point} 
          index={index+1} 
          zIndex={401}
          geometryIndex={0}
        />
      })

      if (ruler.length > 1) {
        interpolatedRuler.push(ruler[ruler.length-1]);
      }

      const points = ruler.map((point, index) => {
        let icon;
        if ( MapType === TILE_LIBRARY_TYPES.JSLA ) {
          icon = createHerePoint();
        } else if ( MapType === TILE_LIBRARY_TYPES.LEAFLET ) {
          icon = createLeafletIcon(colorPalette.primary, index);
        }
        return <MarkerComponent 
          key={index} 
          icon={icon}
          hideOnDrag={true}
          position={point} 
          index={index+1} 
          geometryIndex={0}
          zIndex={402}
          draggable={tabIndex === selectedTab ? true : false} 
          resizable={tabIndex === selectedTab ? true : false}
          onChange={tabIndex === selectedTab ? this.onChangePoint.bind(this, index) : null}
          onClick={tabIndex === selectedTab ? this.onClickPoint.bind(this, index) : null}
        />
      })

      return <>
        <LinesComponent
          positions={interpolatedRuler}
          strokeColor={colorPalette.primary}
          zIndex={400}
          lineWidth={2}
          onChange={()=>{}}
        />
        {texts}
        {points}
      </>;
    }
  };
}

export default DistanceMeasurement;
